import React, {FC, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {
  useTimeout,
  useToggle,
} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {tooltipDefaultDelay} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipHelpers';
import {ApplicationModel} from '../../../../../../models/api/applicationModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {TopicTypeModel} from '../../../../../../models/api/integrationModel';
import {TopicModel} from '../../../../../../models/api/topicModel';
import {topicDataEventTracker} from '../../../../../../services/tracking/topic';
import {InboxesVisualConversationSelection} from '../../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {IntegrationTopicIcon} from '../../../../../common/integrationTopicIcon';
import {HoverTopicReferencePreviewerUnfurl} from '../../../activity/activityReferencePreviewerPill/hoverTopicReferencePreviewerUnfurl';
import {ConversationSummaryContainer} from '../../conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../../conversationSummary/conversationSummarySections';
import {ConversationHeaderPill, StyledPopoverCoordinatorWrapperDiv} from '../conversationHeaderPill';
import {ConversationHeaderPillText} from '../conversationHeaderPillText';
import {TopicDropdownContainer} from './topicDropdownContainer';
import {useConversationsHandlers} from './useConversationHandlers';

/*
 * Props.
 */

interface ConversationHeaderReferencePreviewerPillProps {
  panelId: ViewerPanelId;
  conversation?: ConversationModel;
  teammateId: number;
  topic: TopicModel;
  application?: ApplicationModel;
  topicType?: TopicTypeModel;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Style.
 */

const StyledIntegrationTopicIcon = styled(IntegrationTopicIcon)`
  margin: 0;
  padding: 0;
`;

const StyledTopicIntegrationWrapper = styled.div`
  width: 19px;
  height: 19px;
  display: inline-block;
  margin: 0px -3px -2px -4px;
`;

const StyledApplicationWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  margin-right: 4px;
`;

const StyledApplicationImg = styled.img`
  width: 14px;
  height: 14px;
`;

const StyledConversationHeaderPillText = styled(ConversationHeaderPillText)`
  text-transform: none;
  color: ${(p) => p.theme.palette.blue.shade40};
`;

/*
 * Component.
 */

export const ConversationHeaderReferencePreviewerPill: FC<ConversationHeaderReferencePreviewerPillProps> = (
  props,
) => {
  const {panelId, conversation, teammateId, topic, conversationSelection, topicType, application} = props;
  const [isConversationSummaryOpen, toggleIsConversationSummaryOpen] = useToggle();
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);

  const isLinkTopic = Boolean(topicType || topic.type === 'web');
  const isApplicationTopic = topic.type.startsWith('app_');
  const conversationsHandlers = useConversationsHandlers(conversationSelection, topic.id);
  const {trackDataEvent} = useDataEventTracking();
  const [setTimeout, clearTimeout] = useTimeout();

  const trackAction = useCallback(
    (action: DataEventActionsEnum) => {
      trackDataEvent(...topicDataEventTracker(action, topic));
    },
    [topic, trackDataEvent],
  );

  if (!isLinkTopic && !isApplicationTopic) {
    return null;
  }

  const maybeRenderConversationSummary = () =>
    isConversationSummaryOpen && conversation ? (
      <ConversationSummaryContainer
        conversation={conversation}
        teammateId={teammateId}
        onRequestClose={toggleIsConversationSummaryOpen}
        initiallyExpandedSection={ConversationSummarySectionsEnum.REFERENCE_PREVIEWERS}
        panelId={panelId}
        selectedTopicId={topic.id}
      />
    ) : null;

  const onPillClick = () => {
    clearTimeout();
    setShouldShowPreview(false);
    toggleIsModalOpen();
    if (isModalOpen) {
      trackAction(DataEventActionsEnum.CLICK);
    }
  };

  const onTopicEnter = () => {
    setTimeout(() => {
      setShouldShowPreview(true);
    }, tooltipDefaultDelay);
  };

  const onTopicLeave = () => {
    clearTimeout();
    setShouldShowPreview(false);
  };

  return (
    <StyledPopoverCoordinatorWrapperDiv onMouseEnter={onTopicEnter} onMouseLeave={onTopicLeave}>
      <ChromelessButton isOpen={isModalOpen} ref={anchor} onClick={onPillClick}>
        <ConversationHeaderPill hasHover={isModalOpen} renderIcon={() => renderIcon(topicType, application)}>
          <StyledConversationHeaderPillText>{topic.name}</StyledConversationHeaderPillText>
        </ConversationHeaderPill>
      </ChromelessButton>
      <HoverTopicReferencePreviewerUnfurl
        topic={topic}
        anchor={anchor}
        shouldShowPreview={shouldShowPreview}
      />
      {maybeRenderConversationSummary()}
      {isModalOpen && (
        <TopicDropdownContainer
          {...props}
          {...conversationsHandlers}
          isApplicationTopic={isApplicationTopic}
          onToggleSummary={toggleIsConversationSummaryOpen}
          onOpenUrl={() => trackAction(DataEventActionsEnum.OPEN_URL)}
          anchor={anchor}
          onRequestClose={toggleIsModalOpen}
        />
      )}
    </StyledPopoverCoordinatorWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderIcon(topicType?: TopicTypeModel, application?: ApplicationModel) {
  if (topicType) {
    return (
      <StyledTopicIntegrationWrapper>
        <StyledIntegrationTopicIcon integrationTopicIconUrl={topicType.image} />
      </StyledTopicIntegrationWrapper>
    );
  }
  if (application && application.iconUrl) {
    return (
      <StyledApplicationWrapperDiv>
        <StyledApplicationImg src={application.iconUrl} />
      </StyledApplicationWrapperDiv>
    );
  }
  return <Icon name="topicWebSmall" />;
}
